/*
    Cache.js - Local cache of database data
 */
import {Feedback} from '@/paks/vu-feedback'
import {State, Store} from '@/paks/vu-state'
import {allow} from '@/paks/js-polyfill'
import {Dashboard} from '@/paks/mgr-models'

/*
    Map of Rest entities to cacheable collections
 */
const CacheMap = {
    Cloud: 'clouds',
    Device: 'devices',
    Dashboard: 'dashboards',
}
const PersistState = ['dashboard']

export default class CacheState {
    clouds = []
    dashboard = {widgets: []}
    dashboards = []
    devices = []
    forms = {}
    lastUpdated = null
    removedDashboards = []

    saveState(state) {
        return allow(state, PersistState)
    }

    loadState(state) {
        Store.addMethods({
            find: this.find.bind(this),
            get: this.get.bind(this),
            remove: this.remove.bind(this),
            set: this.set.bind(this),
        })
        return allow(state, PersistState)
    }

    reset() {
        this.devices = []
        this.dashboards = []
    }

    async update(params = {}) {
        if (this.updating) {
            do {
                //  Inline delay to avoid vu-app import
                await new Promise(resolve => setTimeout(resolve, 100))
            } while (this.updating)
        }
        try {
            for (let [key, value] of Object.entries(params.cache || {})) {
                this[CacheMap[key]] = value || []
            }
            //  REPAIR
            for (let [key, dashboard] of Object.entries(this.dashboards)) {
                this.dashboards[key] = Dashboard.clean(dashboard)
            }
            let name = State.config.features?.dash?.name || this.dashboard?.name
            if (name) {
                this.dashboard = this.dashboards.find(d => d.name == name)
                if (!this.dashboard && this.dashboards.length) {
                    this.dashboard = this.dashboards[0]
                }
            }
            this.lastUpdated = new Date()
        } catch (err) {
            Feedback.error('Network or service error')
        } finally {
            this.updating = false
        }
        if (this.devices.length == 1) {
            State.context.setKey('deviceId', this.devices[0].id)
        }
    }

    get(model, fields) {
        if (fields == undefined) {
            return null
        }
        model = CacheMap[model]
        let result = this.search(State.cache[model], fields)
        if (result.length == 0) {
            return null
        }
        return result[0]
    }

    find(model, fields) {
        model = CacheMap[model]
        return this.search(State.cache[model], fields)
    }

    remove(model, fields) {
        if (!fields) {
            return
        }
        model = CacheMap[model]
        if (Array.isArray(fields)) {
        } else {
            if (fields.id) {
                let result = this.searchIndexes(State.cache[model], {id: fields.id})
                for (let index of result.reverse()) {
                    State.cache[model].splice(index, 1)
                }
            } else {
                for (let item of this.search(State.cache[model], fields)) {
                    let result = this.searchIndexes(State.cache[model], {id: item.id})
                    for (let index of result.reverse()) {
                        State.cache[model].splice(index, 1)
                    }
                }
            }
        }
    }

    set(model, fields) {
        model = CacheMap[model]
        if (fields == undefined) {
            return
        }
        if (Array.isArray(fields)) {
            State.cache[model] = fields
        } else {
            let result = this.searchIndexes(State.cache[model], {id: fields.id})
            if (result.length == 0) {
                State.cache[model].push(fields)
            } else {
                let index = result[0]
                if (JSON.stringify(State.cache[model][index]) != JSON.stringify(fields)) {
                    State.cache[model][index] = fields
                }
            }
        }
        return fields
    }

    setForm(form, widget) {
        this.forms[form] = widget
    }

    /*
        Search for a record described by the fields and return [index, rec]
     */
    search(grid, fields) {
        grid = grid ? (Array.isArray(grid) ? grid : [grid]) : []
        if (typeof fields == 'string') {
            return grid.filter((i) => i.id == fields || i.name == fields || i.arn == fields)
        } else if (typeof fields == 'function') {
            return grid.filter(fields)
        } else {
            let result = []
            for (let data of Object.values(grid)) {
                if (!fields || Object.keys(fields).every((key) => key == 'source' || data[key] == fields[key])) {
                    result.push(data)
                }
            }
            return result
        }
    }

    searchIndexes(grid, fields) {
        let result = []
        grid = grid ? (Array.isArray(grid) ? grid : [grid]) : []
        for (let [index, data] of Object.entries(grid)) {
            if (!data) continue
            if (!fields || Object.keys(fields).every((key) => key == 'source' || data[key] == fields[key])) {
                result.push(index)
            }
        }
        return result
    }

    get map() {
        return CacheMap
    }
}
